import React, { useState, useEffect } from "react";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import {
    Box,
    Button,
    Card,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    TextField,
    Select,
    FormControl,
    TableContainer,
    Pagination,
    FormHelperText,
} from "@mui/material";
import { GridLegacy as Grid } from "@mui/material";
import Icon from "../../Assets/images/svg/Icons/CategorieIcon.png";
import ActionIcon from "../../Assets/images/png/Action.png";
import SearchandExport from "../../Components/Common/SearchandExport/SearchandExport";
import BoxHeader from "../../Components/Common/Box/Boxheader";
import "../../Components/Common/Styles/Formstyles.css";
import {
    PatchApiMethod,
    DeleteApiMethod,
    PostApiMethod,
    GetApiMethod,
} from "../../Utils/ApiService";
import { SubCategory, useCategory } from "../../Components/Common/Context/Context";

import { useNavigate, useLocation, useParams } from "react-router-dom";
import SuccessDialog from "../../Components/Common/SuccesMessages/SuccessDialog";
import { useSuccessDialog } from "../../Components/Common/SuccesMessages/SuccessDialog";
// Interfaces

const Subcategory = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuRowId, setMenuRowId] = useState<string | null>(null);
    const [newSubCategoryName, setNewSubCategoryName] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [showSubCategoryList, setShowSubCategoryList] = useState<boolean>(true);
    const [editingSubCategoryId, setEditingSubCategoryId] = useState<string | null>(null);
    const [touched, setTouched] = useState(false);
    const [tab, setTab] = useState(0);

    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const { allCategories, allSubcategories, fetchSubcategories } = useCategory();
    const [filteredData, setFilteredData] = useState<SubCategory[]>(allSubcategories);
    const { allCategories: categories, } = useCategory();

    const { open: successOpen, message, showSuccess, closeSuccess } = useSuccessDialog();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    useEffect(() => {
        if (!id) { //  only update filteredData if you're not viewing a single subcategory
            setFilteredData(allSubcategories);
            setCurrentPage(1);
        }
    }, [allSubcategories, id]);

    // Fetch subcategories
    useEffect(() => {
        const loadSubCategory = async () => {
            if (!id) return;

            try {
                const response = await GetApiMethod(`sub-category/${id}`);
                const subCategory = response?.data?.data?.subCategory;

                if (subCategory) {
                    setFilteredData([subCategory]); // Only show the fetched one
                    setCurrentPage(1);
                }
            } catch (err) {
                console.error("Failed to fetch subcategory by ID:", err);
            }
        };

        loadSubCategory();
    }, [id, location.pathname]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData]);
    // Handle add/edit route
    useEffect(() => {
        if (location.pathname.endsWith("/add")) {
            setEditingSubCategoryId(null);
            setNewSubCategoryName("");
            setSelectedCategory("");
            setShowSubCategoryList(false);
        } else if (location.pathname.includes("/edit")) {
            // Only enter form if it's specifically edit route
            setShowSubCategoryList(false);
        } else {
            setShowSubCategoryList(true);
        }
    }, [location.pathname]);

    // Initial fetch
    useEffect(() => {
        fetchSubcategories();
    }, []);
    // Search filter
    useEffect(() => {
        if (!id) { 
            const results = allSubcategories.filter(item =>
                item.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredData(results);
        }
    }, [searchQuery, allSubcategories, id]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
        setAnchorEl(event.currentTarget);
        setMenuRowId(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRowId(null);
    };

    const resetForm = () => {
        setNewSubCategoryName("");
        setSelectedCategory("");
        setTouched(false);
        setEditingSubCategoryId(null);
        setShowSubCategoryList(true);
    };

    const handleAddCategory = async () => {
        setTouched(true); // force validation
        if (!newSubCategoryName.trim() || !selectedCategory) {
            return; // stop submit if invalid
        }

        try {
            setLoading(true);
            if (editingSubCategoryId) {
                await PatchApiMethod(`sub-category/${editingSubCategoryId}`, {
                    name: newSubCategoryName,
                    categoryId: selectedCategory,
                });

                showSuccess("SubCategories updated successfully!");
                navigate("/Subcategories");
            } else {
                await PostApiMethod("sub-category", {
                    name: newSubCategoryName,
                    categoryId: selectedCategory,
                });
                showSuccess("SubCategories Added successfully!");
            }
            await fetchSubcategories();
            resetForm();
        } catch (err) {
            console.error("Error saving subcategory:", err);
        } finally {
            setLoading(false);
        }
    };
    const handleDeleteCategory = async (id: string | null) => {
        if (!id) return;
        try {
            setLoading(true);
            await DeleteApiMethod(`sub-category/${id}`);
            await fetchSubcategories();
            showSuccess("SubCategories deleted successfully!");
        } catch (err) {
            console.error("Error deleting subcategory:", err);
        } finally {
            setLoading(false);
        }
    };
    const handleStatusToggle = async (selected: any) => {
        try {
            const newStatus = selected.status ? 0 : 1;

            await PatchApiMethod(`sub-category/${selected.id}`, { status: newStatus });

            showSuccess(
                `SubCategory "${selected.name}" ${newStatus ? "enabled" : "disabled"} successfully!`
            );

            await fetchSubcategories(); // refresh the table
        } catch (err) {
            console.error("Error updating status:", err);
        } finally {
            handleMenuClose();
        }
    };

    return (
        <AuthorizedLayout>
            <Box sx={{ px: 2, py: 3, fontFamily: "Poppins, Semibold" }} >
                {loading && (
                    <Box className="loading-overlay">
                        <CircularProgress size={50} thickness={5} />
                    </Box>
                )}

                <Card className="card-layout">
                    <BoxHeader
                        dashboard_name="Sub Categories"
                        image={Icon}
                        button_show={true}
                        button_name="New Sub Category"
                        onAdd={() => navigate("/Subcategories/add")}
                        tabs={["All Sub Categories"]}
                        activeTab={tab}
                        onTabChange={(newTab) => setTab(newTab)}
                    />
                    {!showSubCategoryList ? (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                setTouched(true); // mark fields as touched
                                if (!newSubCategoryName.trim() || !selectedCategory) return; // prevent submit if invalid
                                handleAddCategory();
                            }}
                        >
                            <Typography variant="h6" className="form-header" mb={3}>
                                {editingSubCategoryId ? "Edit Sub Category" : "Add New Sub Category"}
                            </Typography>

                            <Grid container spacing={3}>
                                {/* Sub Category Name */}
                                <Grid item xs={12} md={4}>
                                    <Typography className="form-input">Sub Category</Typography>
                                    <TextField
                                        className="form-input"
                                        variant="standard"
                                        placeholder="Type here"
                                        value={newSubCategoryName}
                                        onChange={(e) => setNewSubCategoryName(e.target.value)}
                                        onBlur={() => setTouched(true)}
                                        inputProps={{ maxLength: 255 }}
                                        fullWidth
                                        error={touched && !newSubCategoryName.trim()}
                                        helperText={touched && !newSubCategoryName.trim() ? "Subcategory name is required" : ""}
                                    />
                                </Grid>

                                {/* Category Dropdown */}
                                <Grid item xs={12} md={4}>
                                    <Typography className="form-input">Choose Category</Typography>
                                    <FormControl
                                        variant="standard"
                                        fullWidth
                                        error={touched && !selectedCategory}
                                    >
                                        <Select
                                            className="form-input"
                                            value={selectedCategory}
                                            onChange={(e) => setSelectedCategory(e.target.value)}
                                            onBlur={() => setTouched(true)}
                                            displayEmpty
                                            renderValue={(selected) => {
                                                if (!selected) {
                                                    return (
                                                        <span style={{ color: '#999' /* dimmed grey color */ }}>
                                                            Select Category
                                                        </span>
                                                    );
                                                }
                                                const selectedCat = categories.find((cat) => cat.id === selected);
                                                return selectedCat ? selectedCat.name : '';
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>Select Category</em>
                                            </MenuItem>
                                            {categories.map((cat) => (
                                                <MenuItem key={cat.id} value={cat.id}>
                                                    {cat.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {touched && !selectedCategory && (
                                            <FormHelperText>Please select a category</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                            </Grid>

                            <Box mt={4} display="flex" gap={2} flexWrap="wrap">
                                <Button
                                    onClick={() => {
                                        resetForm();
                                        navigate("/Subcategories");
                                    }}
                                    className="btn-cancel"
                                    type="button"
                                >
                                    Cancel
                                </Button>
                                <Button className="btn-next" type="submit">
                                    {editingSubCategoryId ? "Update" : "Add"}
                                </Button>
                            </Box>
                        </form>
                    ) : (
                        <>
                            <SearchandExport searchPlaceholder="Type here" onSearchChange={setSearchQuery} />

                            {/* Responsive Table */}
                            <TableContainer sx={{ overflowX: "auto" }}>
                                <Table className="table">
                                    <TableHead>
                                        <TableRow className="table-row">
                                            <TableCell>
                                                <div className="inner-cell table-header">Sub Categories</div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="inner-cell  table-header">Categories</div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="inner-cell  table-header">Action</div>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((row) => (
                                                <TableRow
                                                    key={row.id}
                                                    sx={{
                                                        backgroundColor: row.status ? "inherit" : "#f5f5f5", // light background when disabled
                                                    }}
                                                >
                                                    {/* Subcategory Name */}
                                                    <TableCell
                                                        sx={{
                                                            opacity: row.status ? 1 : 0.5, // dim only this cell
                                                        }}
                                                    >
                                                        <div className="inner-cell table-cell">{row.name}</div>
                                                    </TableCell>

                                                    {/* Category */}
                                                    <TableCell
                                                        sx={{
                                                            opacity: row.status ? 1 : 0.5, // dim this cell too
                                                        }}
                                                    >
                                                        <div className="inner-cell table-cell">
                                                            {allCategories.find((cat) => cat.id === row.categoryId)?.name ?? "Unknown Category"}
                                                        </div>
                                                    </TableCell>

                                                    {/* Action column stays normal */}
                                                    <TableCell>
                                                        <div className="inner-cell table-cell">
                                                            <Box display="flex" alignItems="center">
                                                                <Typography className="table-cell action">Action</Typography>
                                                                <IconButton onClick={(e) => handleMenuOpen(e, row.id)}>
                                                                    <img src={ActionIcon} alt="action menu" />
                                                                </IconButton>
                                                            </Box>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>

                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    No subcategories found
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>

                                </Table>
                            </TableContainer>
                            <Box mt={2} display="flex" justifyContent="center">
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={(_, page) => setCurrentPage(page)}
                                    color="primary"
                                    siblingCount={1}
                                    boundaryCount={0}
                                    showFirstButton={false}
                                    showLastButton={false}
                                />
                            </Box>
                        </>
                    )}
                    <>
                        {/* Action Menu */}
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "left",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            {(() => {
                                const selected = filteredData.find((c) => c.id === menuRowId);
                                if (!selected) return null;

                                const isDisabled = selected.status === false;

                                return (
                                    <>
                                        {/*Show Edit/Delete only when enabled */}
                                        {!isDisabled && (
                                            <>
                                                <MenuItem
                                                    onClick={() => {
                                                        setEditingSubCategoryId(selected.id);
                                                        setNewSubCategoryName(selected.name);
                                                        setSelectedCategory(selected.categoryId);
                                                        setShowSubCategoryList(false);
                                                        navigate(`/Subcategory/${selected.id}/edit`);
                                                        handleMenuClose();
                                                    }}
                                                    className="edit-menu-item"
                                                >
                                                    Edit
                                                </MenuItem>

                                                <MenuItem
                                                    onClick={() => {
                                                        handleDeleteCategory(menuRowId);
                                                        handleMenuClose();
                                                    }}
                                                    className="delete-menu-item"
                                                >
                                                    Delete
                                                </MenuItem>
                                            </>
                                        )}

                                        {/* Always show Enable/Disable toggle */}
                                        <MenuItem
                                            onClick={() => handleStatusToggle(selected)} // clean call
                                            className={selected.status ? "disable-menu-item" : "enable-menu-item"}
                                        >
                                            {selected.status ? "Disable" : "Enable"}
                                        </MenuItem>
                                    </>
                                );
                            })()}
                        </Menu>
                    </>
                </Card>
            </Box>
            <SuccessDialog open={successOpen} onClose={closeSuccess} message={message} />
        </AuthorizedLayout>
    );
};
export default Subcategory;
